import React, { useState } from 'react';
import { z } from 'zod';

import { If } from 'utils';
import { StoreConnector, UiKit } from 'components';
import { useFetch, useForm } from 'utils/hooks';
import { getSafeRedirectUrl } from 'utils/methods';
import { useConnectForm } from '../hooks';
import { STORE_URL_PREFIX, STORE_URL_SUFFIX } from '../stubs';

export const FORM_SCHEMA = z.object({
  storeUrl: z
    .string()
    .url('Please provide a valid URL')
    .min(1, 'Store URL is required')
});

export default function WoocommerceConnect() {
  const [oAuthURL, setOAuthURL] = useState('');

  const { connect } = useConnectForm();

  const {
    data,
    errors,
    isDirty,
    onChange,
    onValidate,
    onSetInitialData
  } = useForm({
    dataSchema: FORM_SCHEMA,
    defaultData: { storeUrl: '' }
  });

  const { isLoading: isConnecting, fetchData } = useFetch('storeConnect.woo', {
    loadOnMount: false,
    isPublicAPI: true,
    errorMessage: 'Unable to connect to WooCommerce store. Please try again.',
    onSuccess: ({ redirect_url = '' }) => {
      const safeUrl = getSafeRedirectUrl(redirect_url) || '';
      setOAuthURL(safeUrl);
      // Thrown errors are caught by useFetch, which shows errorMessage
      if (!safeUrl) throw new Error('Invalid WooCommerce authorize URL');
      onSetInitialData(data);
    }
  });

  const onSubmit = e => {
    e.preventDefault();

    const isValid = onValidate(data);
    if (!isValid) return;

    connect(
      {
        shop: `${STORE_URL_PREFIX.WOOCOMMERCE}${data.storeUrl}${STORE_URL_SUFFIX.WOOCOMMERCE}`
      },
      (_, payload) => fetchData(payload)
    );
  };

  const onUrlChange = value => {
    onChange({ storeUrl: value }, { runValidation: !!errors.storeUrl });
  };

  return (
    <>
      <form onSubmit={onSubmit}>
        <StoreConnector.UrlInput
          disablePreview
          name="storeUrl"
          value={data.storeUrl}
          onChange={onUrlChange}
          label="Woo Store URL"
          placeholder="Enter your store URL"
          prefix={STORE_URL_PREFIX.WOOCOMMERCE}
          suffix={STORE_URL_SUFFIX.WOOCOMMERCE}
          disabled={isConnecting}
          error={errors.storeUrl}
        />
        <UiKit.Button
          disabled={!isDirty}
          processing={isConnecting}
          className="w-100"
        >
          Initiate WooCommerce Store Connection
        </UiKit.Button>
      </form>
      <If test={oAuthURL && !isConnecting && !isDirty}>
        <UiKit.Grid templateColumns="1fr" className="mt-3">
          <UiKit.Title size="sm">Store connection initiated!</UiKit.Title>
          <span>
            You will be redirected to external site for authentication.
          </span>
          <a href={oAuthURL}>
            <UiKit.Button className="mt-3 w-100">
              Continue to WooCommerce
            </UiKit.Button>
          </a>
        </UiKit.Grid>
      </If>
    </>
  );
}
